/**
 * Modular Chart System - Utility Functions
 *
 * Helper functions for chart data transformation and configuration
 */

import {
  ChartColorConfig,
  ChartDataPoint,
  ChartDatasetConfig,
  ChartAnnotation,
  ChartJSData,
  ChartJSOptions,
  MetricChartConfig,
  GoalChartConfig
} from './ChartTypes';

/**
 * Default color palette
 */
export const DEFAULT_COLORS: ChartColorConfig = {
  primary: '#3b82f6',       // Blue
  secondary: '#8b5cf6',     // Purple
  success: '#10b981',       // Green
  danger: '#ef4444',        // Red
  warning: '#eab308',       // Yellow
  info: '#06b6d4',          // Cyan
  neutral: '#9ca3af',       // Gray
  target: '#6366f1',        // Indigo
  varianceBand: 'rgba(99, 102, 241, 0.1)' // Light indigo
};

/**
 * Get point color based on whether target was met
 */
export const getPointColorByStatus = (
  metTarget: boolean | null,
  colors: ChartColorConfig = DEFAULT_COLORS
): string => {
  if (metTarget === null) return colors.neutral || DEFAULT_COLORS.neutral!;
  return metTarget ? (colors.success || DEFAULT_COLORS.success!) : (colors.danger || DEFAULT_COLORS.danger!);
};

/**
 * Transform dataset config to Chart.js format
 */
export const transformDatasetToChartJS = (
  dataset: ChartDatasetConfig,
  chartType: string,
  colors: ChartColorConfig = DEFAULT_COLORS
): any => {
  const values = dataset.data.map(point => point.value);

  // Generate point colors if target status is available
  const pointColors = dataset.pointColors || dataset.data.map(point =>
    point.metTarget !== undefined
      ? getPointColorByStatus(point.metTarget, colors)
      : (dataset.color || colors.primary || DEFAULT_COLORS.primary!)
  );

  const baseConfig: any = {
    label: dataset.label,
    data: values,
    backgroundColor: chartType === 'doughnut' || chartType === 'pie' ? pointColors : dataset.color || colors.primary,
    borderColor: dataset.color || colors.primary,
  };

  // Line chart specific
  if (chartType === 'line' || chartType === 'area') {
    baseConfig.pointBackgroundColor = pointColors;
    baseConfig.pointBorderColor = pointColors;
    baseConfig.pointHoverBackgroundColor = pointColors;
    baseConfig.pointHoverBorderColor = pointColors;
    baseConfig.pointRadius = dataset.pointRadii || (dataset.showPoints !== false ? 6 : 0);
    baseConfig.pointHoverRadius = dataset.showPoints !== false ? 8 : 0;
    baseConfig.tension = dataset.tension !== undefined ? dataset.tension : 0.3;
    baseConfig.fill = dataset.fill !== undefined ? dataset.fill : (chartType === 'area');
    if (dataset.borderDash) baseConfig.borderDash = dataset.borderDash;
    if (dataset.borderWidth !== undefined) baseConfig.borderWidth = dataset.borderWidth;
    baseConfig.spanGaps = dataset.spanGaps !== undefined ? dataset.spanGaps : true;
    if (dataset.order !== undefined) baseConfig.order = dataset.order;

    if (dataset.fill === false) {
      baseConfig.backgroundColor = 'transparent';
    } else if (chartType === 'area') {
      baseConfig.backgroundColor = `${dataset.color || colors.primary}40`; // 25% opacity
    }

    // Add segment styling to detect gaps/breaks (skip for helper datasets like goal lines)
    if (!dataset.hidden) baseConfig.segment = {
      borderColor: (ctx: any) => {
        const p0 = ctx.p0;
        const p1 = ctx.p1;

        // Get data points for both ends of the segment
        const p0Data = dataset.data[p0.parsed.x];
        const p1Data = dataset.data[p1.parsed.x];

        // Check if either point is a gap (missing data)
        // This handles both: missing data points AND points marked as gaps
        const p0IsNull = p0Data?.value === null || p0Data?.value === undefined;
        const p1IsNull = p1Data?.value === null || p1Data?.value === undefined;
        const isGapSegment = p0Data?.metadata?.isGapSegment || p1Data?.metadata?.isGapSegment;

        // Yellow segment if: spanning null data OR explicitly marked as gap
        if (p0IsNull || p1IsNull || isGapSegment) {
          return colors.warning || DEFAULT_COLORS.warning; // Yellow
        }

        return dataset.color || colors.primary;
      },
      borderWidth: (ctx: any) => {
        const p0Data = dataset.data[ctx.p0.parsed.x];
        const p1Data = dataset.data[ctx.p1.parsed.x];

        const p0IsNull = p0Data?.value === null || p0Data?.value === undefined;
        const p1IsNull = p1Data?.value === null || p1Data?.value === undefined;
        const isGapSegment = p0Data?.metadata?.isGapSegment || p1Data?.metadata?.isGapSegment;

        // Make gap segments slightly thinner
        if (p0IsNull || p1IsNull || isGapSegment) {
          return 2;
        }

        return 3;
      }
    };
  }

  // Bar chart specific
  if (chartType === 'bar') {
    baseConfig.backgroundColor = pointColors;
    baseConfig.borderWidth = 2;
  }

  return baseConfig;
};

/**
 * Fill the time axis so every date/week between the first and last data point
 * has an explicit x-axis entry. Missing entries get null values, and the
 * segments connecting across them are marked as gaps (rendered yellow).
 *
 * This prevents the chart from "compressing" gaps — missing time is shown as
 * real empty space on the x-axis with a yellow connecting line.
 */
const fillTimeAxis = (
  data: ChartDataPoint[],
  mode: 'daily' | 'weekly'
): ChartDataPoint[] => {
  if (data.length < 2) return data;

  if (mode === 'daily') {
    // Build a lookup of existing data by ISO date
    const byDate = new Map<string, ChartDataPoint>();
    for (const point of data) {
      const key = point.metadata?.date || point.label;
      byDate.set(key, point);
    }

    // Walk every calendar day from first to last
    const firstDate = data[0].metadata?.date || data[0].label;
    const lastDate = data[data.length - 1].metadata?.date || data[data.length - 1].label;
    const start = new Date(firstDate + 'T00:00:00');
    const end = new Date(lastDate + 'T00:00:00');
    const filled: ChartDataPoint[] = [];
    let prevHadData = true;

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const iso = d.toISOString().split('T')[0];
      const existing = byDate.get(iso);

      if (existing) {
        // Real data point — mark as gap segment if previous was missing
        if (!prevHadData) {
          if (!existing.metadata) existing.metadata = {};
          existing.metadata.isGapSegment = true;
        }
        filled.push(existing);
        prevHadData = true;
      } else {
        // Missing day — insert null placeholder
        const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        filled.push({
          label,
          value: null,
          metadata: { date: iso, isGapSegment: true }
        });
        prevHadData = false;
      }
    }
    return filled;

  } else {
    // Weekly mode: fill missing week keys
    // Parse year and week from "YYYY-WNN" format
    const parseWeekKey = (wk: string) => {
      const parts = wk.split('-W');
      return { year: parseInt(parts[0]), week: parseInt(parts[1]) };
    };
    const makeWeekKey = (year: number, week: number) =>
      `${year}-W${String(week).padStart(2, '0')}`;

    const byWeek = new Map<string, ChartDataPoint>();
    for (const point of data) {
      const key = point.metadata?.week_key || point.label;
      byWeek.set(key, point);
    }

    const firstWk = parseWeekKey(data[0].metadata?.week_key || data[0].label);
    const lastWk = parseWeekKey(data[data.length - 1].metadata?.week_key || data[data.length - 1].label);

    const filled: ChartDataPoint[] = [];
    let prevHadData = true;
    let { year, week } = firstWk;

    while (year < lastWk.year || (year === lastWk.year && week <= lastWk.week)) {
      const key = makeWeekKey(year, week);
      const existing = byWeek.get(key);

      if (existing) {
        if (!prevHadData) {
          if (!existing.metadata) existing.metadata = {};
          existing.metadata.isGapSegment = true;
        }
        filled.push(existing);
        prevHadData = true;
      } else {
        filled.push({
          label: key,
          value: null,
          metadata: { week_key: key, isGapSegment: true }
        });
        prevHadData = false;
      }

      // Advance to next week (ISO weeks go up to 52 or 53)
      week++;
      if (week > 52) {
        // Check if week 53 exists for this year (last day of year is Thursday)
        const dec31 = new Date(year, 11, 31);
        const maxWeek = dec31.getDay() === 4 || (dec31.getDay() === 5 && isLeapYear(year)) ? 53 : 52;
        if (week > maxWeek) {
          week = 1;
          year++;
        }
      }
    }
    return filled;
  }
};

const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

/**
 * Generate Chart.js data object
 */
export const generateChartJSData = (
  config: MetricChartConfig
): ChartJSData => {
  // Fill time axis and mark gaps before transforming to Chart.js format
  if (config.gapDetection && (config.type === 'line' || config.type === 'area')) {
    config.datasets.forEach(dataset => {
      dataset.data = fillTimeAxis(dataset.data, config.gapDetection!);
    });
  }

  const labels = config.datasets[0]?.data.map(point => point.label) || [];

  const datasets = config.datasets.map(dataset =>
    transformDatasetToChartJS(dataset, config.type, config.colors)
  );

  return {
    labels,
    datasets
  };
};

/**
 * Generate Chart.js annotations
 */
export const generateChartAnnotations = (
  annotations: ChartAnnotation[] = []
): any => {
  const annotationsObject: any = {};

  annotations.forEach((annotation, index) => {
    const key = `annotation_${index}`;

    if (annotation.type === 'line') {
      annotationsObject[key] = {
        type: 'line' as const,
        yMin: annotation.value,
        yMax: annotation.value,
        borderColor: annotation.color || DEFAULT_COLORS.target,
        borderWidth: annotation.borderWidth || 2,
        borderDash: annotation.borderDash || [5, 5],
        label: annotation.label ? {
          content: annotation.label.content,
          enabled: annotation.label.enabled,
          position: annotation.label.position || 'end',
          backgroundColor: annotation.color || DEFAULT_COLORS.target,
          color: '#ffffff',
          padding: 4,
          borderRadius: 3,
        } : undefined
      };
    }

    if (annotation.type === 'box') {
      annotationsObject[key] = {
        type: 'box' as const,
        yMin: annotation.yMin,
        yMax: annotation.yMax,
        backgroundColor: annotation.backgroundColor || DEFAULT_COLORS.varianceBand,
        borderWidth: annotation.borderWidth || 0,
      };
    }
  });

  return annotationsObject;
};

/**
 * Generate Chart.js options
 */
export const generateChartJSOptions = (
  config: MetricChartConfig
): ChartJSOptions => {
  const options: any = {
    responsive: config.responsive !== false,
    maintainAspectRatio: config.maintainAspectRatio !== undefined ? config.maintainAspectRatio : false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    /**
     * GLOBAL ANIMATION CONFIGURATION
     * Applies to ALL charts using the modular chart system
     * Duration reduced from Chart.js default (1000ms) for snappier UI
     */
    animation: {
      duration: 0,  // Instant render - no animation delay
    },
    plugins: {
      title: {
        display: !!config.title,
        text: config.title || '',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
      legend: {
        display: config.showLegend !== false,
        labels: {
          filter: (item: any) => {
            // Hide datasets marked as hidden from the legend
            const dataset = config.datasets[item.datasetIndex];
            return !dataset?.hidden;
          }
        }
      },
      annotation: {
        annotations: generateChartAnnotations(config.annotations)
      }
    }
  };

  // Add custom tooltip if provided
  if (config.customTooltip) {
    options.plugins.tooltip = {
      filter: function(tooltipItem: any) {
        // Hide tooltip for hidden/helper datasets (e.g. goal boundary lines)
        const dataset = config.datasets[tooltipItem.datasetIndex];
        return !dataset?.hidden;
      },
      callbacks: {
        label: function(context: any) {
          const dataset = config.datasets[context.datasetIndex];
          const dataPoint = dataset.data[context.dataIndex];
          return config.customTooltip!(dataPoint, dataset);
        }
      }
    };
  }

  // Add scales for non-pie/doughnut charts
  if (config.type !== 'pie' && config.type !== 'doughnut') {
    // Calculate dynamic Y-axis range with padding
    const yAxisConfig: any = {
      display: true,
      grid: {
        color: 'rgba(128, 128, 128, 0.3)',
      },
      title: {
        display: !!config.yAxisLabel,
        text: config.yAxisLabel || '',
      },
    };

    // If yAxisBeginAtZero is explicitly false, or if we have goal data, calculate dynamic range
    if (config.yAxisBeginAtZero === false || 'goalValue' in config) {
      const allValues: number[] = [];

      // Collect all data values
      config.datasets.forEach(dataset => {
        dataset.data.forEach(point => {
          if (point.value !== null && point.value !== undefined) {
            allValues.push(point.value);
          }
        });
      });

      // Add goal value and variance/range bounds if available
      if ('goalValue' in config) {
        const goalConfig = config as GoalChartConfig;
        allValues.push(goalConfig.goalValue);

        if (goalConfig.floorValue !== undefined) {
          allValues.push(goalConfig.floorValue);
        }

        if (goalConfig.variancePercent && goalConfig.variancePercent > 0) {
          const lowerBound = goalConfig.goalValue - (goalConfig.goalValue * goalConfig.variancePercent / 100);
          const upperBound = goalConfig.goalValue + (goalConfig.goalValue * goalConfig.variancePercent / 100);
          allValues.push(lowerBound, upperBound);
        }
      }

      if (allValues.length > 0) {
        const minValue = Math.min(...allValues);
        const maxValue = Math.max(...allValues);
        let range = maxValue - minValue;

        // Enforce minimum Y-axis range if specified (prevents over-zoom on tight data)
        let effectiveMin = minValue;
        let effectiveMax = maxValue;
        if (config.yAxisMinRange && range < config.yAxisMinRange) {
          const midpoint = (minValue + maxValue) / 2;
          effectiveMin = midpoint - config.yAxisMinRange / 2;
          effectiveMax = midpoint + config.yAxisMinRange / 2;
          range = config.yAxisMinRange;
        }

        // Add 10% padding above and below for breathing room
        const padding = range * 0.1 || 10;

        yAxisConfig.min = Math.max(0, effectiveMin - padding); // Don't go below 0
        yAxisConfig.max = effectiveMax + padding;
      }

      // Explicit overrides from zoom or manual configuration
      if (config.yAxisMin !== undefined) yAxisConfig.min = config.yAxisMin;
      if (config.yAxisMax !== undefined) yAxisConfig.max = config.yAxisMax;
    } else {
      // Default behavior: start from zero
      yAxisConfig.beginAtZero = true;
    }

    options.scales = {
      x: {
        display: true,
        grid: {
          color: 'rgba(128, 128, 128, 0.3)',
        },
        title: {
          display: !!config.xAxisLabel,
          text: config.xAxisLabel || '',
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 12, // Limit visible labels for cleaner display
          maxRotation: 0,    // Keep labels horizontal
          minRotation: 0,
          padding: 4,
        },
      },
      y: yAxisConfig,
    };

    // Add layout padding to prevent label overflow
    options.layout = {
      padding: {
        left: 8,
        right: 8,
        bottom: 16,
      }
    };
  }

  return options;
};

/**
 * Build goal-specific annotations (target line + variance band)
 */
export const buildGoalAnnotations = (
  goalConfig: GoalChartConfig
): ChartAnnotation[] => {
  const annotations: ChartAnnotation[] = [];

  // Target/ceiling line
  if (goalConfig.showTargetLine !== false) {
    const lineLabel = goalConfig.goalType === 'range'
      ? `Ceiling: ${goalConfig.goalValue} ${goalConfig.unit}`
      : `Target: ${goalConfig.goalValue} ${goalConfig.unit}`;
    annotations.push({
      type: 'line',
      value: goalConfig.goalValue,
      color: goalConfig.colors?.target || DEFAULT_COLORS.target,
      borderWidth: 2,
      borderDash: [5, 5],
      label: {
        content: lineLabel,
        enabled: true,
        position: 'end'
      }
    });
  }

  // Floor line (for range-type goals)
  if (
    goalConfig.goalType === 'range' &&
    goalConfig.floorValue !== undefined
  ) {
    annotations.push({
      type: 'line',
      value: goalConfig.floorValue,
      color: goalConfig.colors?.target || DEFAULT_COLORS.target,
      borderWidth: 2,
      borderDash: [5, 5],
      label: {
        content: `Floor: ${goalConfig.floorValue} ${goalConfig.unit}`,
        enabled: true,
        position: 'start'
      }
    });
  }

  // Range band (for range-type goals — shaded zone between floor and ceiling)
  if (
    goalConfig.showVarianceBand !== false &&
    goalConfig.goalType === 'range' &&
    goalConfig.floorValue !== undefined
  ) {
    annotations.push({
      type: 'box',
      yMin: goalConfig.floorValue,
      yMax: goalConfig.goalValue,
      backgroundColor: goalConfig.colors?.varianceBand || DEFAULT_COLORS.varianceBand
    });
  }

  // Variance band (only for target-type goals)
  if (
    goalConfig.showVarianceBand !== false &&
    goalConfig.goalType === 'target' &&
    goalConfig.variancePercent &&
    goalConfig.variancePercent > 0
  ) {
    const lowerBound = goalConfig.goalValue - (goalConfig.goalValue * goalConfig.variancePercent / 100);
    const upperBound = goalConfig.goalValue + (goalConfig.goalValue * goalConfig.variancePercent / 100);

    annotations.push({
      type: 'box',
      yMin: lowerBound,
      yMax: upperBound,
      backgroundColor: goalConfig.colors?.varianceBand || DEFAULT_COLORS.varianceBand
    });
  }

  return annotations;
};

/**
 * Build default tooltip for goal charts
 */
export const buildGoalTooltip = (
  goalConfig: GoalChartConfig
) => {
  return (dataPoint: ChartDataPoint, _dataset: ChartDatasetConfig): string[] => {
    const actual = dataPoint.value !== null ? `${dataPoint.value} ${goalConfig.unit}` : 'No data';
    const status = dataPoint.metTarget === null ? 'No data' : dataPoint.metTarget ? '✅ Met' : '❌ Missed';

    if (goalConfig.goalType === 'range' && goalConfig.floorValue !== undefined) {
      return [
        `Actual: ${actual}`,
        `Range: ${goalConfig.floorValue} – ${goalConfig.goalValue} ${goalConfig.unit}`,
        `Status: ${status}`,
      ];
    }

    const target = dataPoint.target !== undefined ? `${dataPoint.target} ${goalConfig.unit}` : `${goalConfig.goalValue} ${goalConfig.unit}`;
    return [
      `Actual: ${actual}`,
      `Target: ${target}`,
      `Status: ${status}`,
    ];
  };
};
